import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Modal } from "react-bootstrap";
import { ActionButton, AltButton } from "../../../common/Buttons/Buttons.jsx";
import { notify } from "../../../../assets/helpers/toast";
import { operationsCommissionService } from "../../../../services/operationsCommissionService";
import * as schemas from "../../../../assets/schemas/schemas";
import {
  RhfDateYmdField,
  RhfMoneyField,
  RhfNumberFieldWithSuffix,
  toYMD,
} from "../../../common/RhfParameterInputs/RhfParameterInputs.jsx";
import {
  operationsCommissionDefaultValues,
  operationsCommissionFormKeys,
  formatApplicationDateForModal,
} from "../../../common/RhfParameterInputs/rhfParameterInputsShared.js";
import { OperationsCommissionHistorySection } from "./OperationsCommissionHistorySection.jsx";


const CommissionConfigForm = ({ onSave, handleCancel }) => {
  const { handleSubmit } = useFormContext();
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [pendingSavePayload, setPendingSavePayload] = useState(null);

  const requestSave = useCallback(() => {
    handleSubmit((data) => {
      setPendingSavePayload(data);
      setConfirmSaveOpen(true);
    })();
  }, [handleSubmit]);

  const closeConfirmSave = useCallback(() => {
    setConfirmSaveOpen(false);
    setPendingSavePayload(null);
  }, []);

  const confirmSave = useCallback(async () => {
    if (!pendingSavePayload) return;
    const ok = await onSave(pendingSavePayload);
    if (ok) closeConfirmSave();
  }, [onSave, pendingSavePayload, closeConfirmSave]);

  const applicationDateLabel = formatApplicationDateForModal(
    pendingSavePayload?.dateSince
  );

  return (
    <>
      <form
        className="baremo-section__form"
        onSubmit={(e) => {
          e.preventDefault();
          requestSave();
        }}
        noValidate
      >
        <div className="baremo-section__date-box">
          <div className="baremo-section__date-row">
            <RhfDateYmdField
              name="dateSince"
              label="Fecha de aplicación"
              id="commission-dateSince"
              idPrefix="commission"
            />
            <p className="baremo-section__date-hint">
              <span className="baremo-section__date-hint--muted">
                Los cambios aplicarán a partir de la fecha seleccionada.{" "}
              </span>
              <span className="baremo-section__date-hint--accent">
                Los servicios anteriores no serán afectados.
              </span>
            </p>
          </div>
        </div>

        <div className="baremo-section__subsection">
          <h3 className="baremo-section__subsection-title">
            Comisiones por Servicio Particular
          </h3>
          <div className="baremo-section__grid baremo-section__grid--3">
            <RhfMoneyField
              idPrefix="commission"
              name="roleOperator"
              label="Operador"
              suffix="$"
              description="Por cada servicio particular"
            />
            <RhfMoneyField
              idPrefix="commission"
              name="roleSuperv"
              label="Supervisor"
              suffix="$"
              description="Por cada servicio particular"
            />
            <RhfMoneyField
              idPrefix="commission"
              name="roleCoord"
              label="Coordinador"
              suffix="$"
              description="Por cada servicio particular"
            />
          </div>
        </div>

        <div className="baremo-section__volume-bonus">
          <h3 className="baremo-section__volume-bonus-title">
            Bonificación por volumen (Operador)
          </h3>
          <div className="baremo-section__volume-bonus-row">
            <span className="baremo-section__volume-bonus-text">
              A partir del servicio N°
            </span>
            <div className="baremo-section__volume-bonus-field baremo-section__volume-bonus-field--since">
              <RhfNumberFieldWithSuffix
                idPrefix="commission"
                name="roleOperatorExtraSince"
                label="Número de servicio (umbral en el mes)"
                suffix=""
                step="1"
                min={0}
                inputMode="numeric"
              />
            </div>
            <span className="baremo-section__volume-bonus-text">
              del mes, el operador cobra
            </span>
            <div className="baremo-section__volume-bonus-field baremo-section__volume-bonus-field--amount">
              <RhfMoneyField
                idPrefix="commission"
                name="roleOperatorExtraAmount"
                label="Monto extra por servicio"
                suffix="$"
              />
            </div>
            <span className="baremo-section__volume-bonus-text baremo-section__volume-bonus-text--muted">
              extra por servicio
            </span>
          </div>
        </div>

        <div className="baremo-section__subsection">
          <h3 className="baremo-section__subsection-title">
            Otras comisiones
          </h3>
          <div className="baremo-section__grid baremo-section__grid--2">
            <RhfMoneyField
              idPrefix="commission"
              name="solvedByAv"
              label="Avería resuelta"
              suffix="$"
              description="Cuando el asistente vial resuelve una avería en sitio"
            />
            <RhfMoneyField
              idPrefix="commission"
              name="battery"
              label="Venta de batería"
              suffix="$"
              description="Por cada servicio de venta de batería concretado"
            />
          </div>
        </div>

        <div className="parameters-page__alarm-footer">
          <AltButton label="Cancelar" action={handleCancel} />
          <ActionButton label="Guardar cambios" action={requestSave} />
        </div>
      </form>

      <Modal
        centered
        show={confirmSaveOpen}
        onHide={closeConfirmSave}
        className="baremo-section__confirm-modal"
      >
        <Modal.Header closeButton className="modal-header">
          <Modal.Title as="h5">Confirmar guardado</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-body">
          <p className="baremo-section__confirm-modal-text mb-0">
            Los cambios surtirán efecto a partir del{" "}
            <strong>{applicationDateLabel}</strong>, de acuerdo con la{" "}
            <strong>fecha de aplicación</strong> seleccionada en el formulario.
          </p>
        </Modal.Body>
        <Modal.Footer className="modal-footer baremo-section__confirm-modal-footer">
          <AltButton label="Cancelar" action={closeConfirmSave} />
          <ActionButton label="Guardar" action={confirmSave} />
        </Modal.Footer>
      </Modal>
    </>
  );
};

export const OperationsCommissionParametersSection = () => {
  const [tab, setTab] = useState("config");
  const [loading, setLoading] = useState(true);
  const [formReady, setFormReady] = useState(false);
  const [lastApiData, setLastApiData] = useState(null);
  const savedFormSnapshotRef = useRef(null);

  const commissionSchema = useMemo(
    () => schemas.operationsCommissionFormSchema(),
    []
  );

  const formMethods = useForm({
    defaultValues: operationsCommissionDefaultValues(),
    resolver: yupResolver(commissionSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const { reset, getValues } = formMethods;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const response = await operationsCommissionService.getLast();
      if (cancelled) return;
      setLoading(false);

      setLastApiData(response?.data != null ? response.data : null);

      if (response?.status === 200 && response.data) {
        const base = operationsCommissionDefaultValues();
        const src = response.data?.data ?? response.data;
        const next =
          src && typeof src === "object"
            ? Object.fromEntries(
              operationsCommissionFormKeys.map((k) => [
                k,
                k in src
                  ? k === "dateSince"
                    ? toYMD(src[k]) || base.dateSince
                    : src[k]
                  : base[k],
              ])
            )
            : base;
        reset(next);
        savedFormSnapshotRef.current = JSON.parse(JSON.stringify(next));
        setFormReady(true);
        return;
      }
      if (response?.status && response.status !== 200) {
        notify(
          response?.message || "No se pudo cargar la configuración de comisiones",
          "error"
        );
      }
      const fallback = operationsCommissionDefaultValues();
      reset(fallback);
      savedFormSnapshotRef.current = JSON.parse(JSON.stringify(fallback));
      setFormReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [reset]);

  const handleCancel = useCallback(() => {
    if (savedFormSnapshotRef.current) {
      reset(JSON.parse(JSON.stringify(savedFormSnapshotRef.current)));
    } else {
      reset(operationsCommissionDefaultValues());
    }
  }, [reset]);

  const onSave = useCallback(
    async (formData) => {
      const payload = Object.fromEntries(
        operationsCommissionFormKeys
          .filter((k) => k !== "dateSince")
          .map((k) => [k, formData[k]])
      );
      payload.dateSince = formData.dateSince;

      const response = await operationsCommissionService.save(payload);
      if (response?.status === 200) {
        const snapshot = { ...getValues() };
        savedFormSnapshotRef.current = JSON.parse(JSON.stringify(snapshot));
        notify("Cambios guardados", "success");
        return true;
      }
      notify(
        response?.message || "No se pudieron guardar los parámetros",
        "error"
      );
      return false;
    },
    [getValues]
  );

  if (loading || !formReady) {
    return (
      <div className="baremo-section baremo-section--loading">
        Cargando parámetros…
      </div>
    );
  }

  return (
    <div
      className="baremo-section"
      data-api-payload={lastApiData != null ? "1" : "0"}
    >
      <FormProvider {...formMethods}>
        <div className="baremo-section__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "config"}
            className={`baremo-section__tab ${tab === "config" ? "baremo-section__tab--active" : ""
              }`}
            onClick={() => setTab("config")}
          >
            Configuración actual
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "history"}
            className={`baremo-section__tab ${tab === "history" ? "baremo-section__tab--active" : ""
              }`}
            onClick={() => setTab("history")}
          >
            Historial de cambios
          </button>
        </div>

        {tab === "history" ? (
          <OperationsCommissionHistorySection />
        ) : (
          <CommissionConfigForm onSave={onSave} handleCancel={handleCancel} />
        )}
      </FormProvider>
    </div>
  );
};
